import { Callout, Card, LoadingRegion, Skeleton } from '@apovisio/ui';
import { guestEventSchema } from '@apovisio/contracts';
import type { GuestEvent, GuestState, LivekitConnection } from '@apovisio/contracts';
import { useCallback, useEffect, useRef, useState } from 'react';
import { BrandMark } from '../../app/BrandMark';
import { PageHeading } from '../../components/PageHeading';
import {
  cancelAdmission,
  currentGuestSession,
  openGuestSession,
  requestAdmission,
} from '../../features/guest/api';
import { GuestMessage, RetryButton } from '../../features/guest/GuestMessage';
import type { GuestEnd } from '../../features/guest/GuestMessage';
import { loadDevicePreferences } from '../../features/guest/device-preferences';
import { PrejoinView } from '../../features/guest/PrejoinView';
import { WaitingView } from '../../features/guest/WaitingView';
import { fetchGuestToken, leaveAsGuest } from '../../features/room/api';
import { ConnectionLost, RoomView, disconnectOutcome } from '../../features/room/RoomView';
import { ApiClientError } from '../../lib/api';
import { useEventStream } from '../../lib/event-stream';

type Phase =
  { kind: 'loading' } | { kind: 'ended'; reason: GuestEnd } | { kind: 'ready'; state: GuestState };

function endReason(error: unknown): GuestEnd {
  if (error instanceof ApiClientError) return error.network ? 'NETWORK' : error.errorCode;
  return 'INTERNAL_ERROR';
}

/** Lit puis efface le secret du fragment (#k=…), pour qu'il ne reste pas visible à l'écran. */
function takeSecretFromLocation(): string | null {
  const secret = new URLSearchParams(window.location.hash.slice(1)).get('k');
  if (secret) window.history.replaceState(null, '', window.location.pathname);
  return secret;
}

/** Parcours invité : pré-join → demande → attente → admission (salle au Sprint 5). */
export function GuestPage() {
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [requestError, setRequestError] = useState<string | null>(null);
  const [requesting, setRequesting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  /** Après un retrait (sans révocation), l'invité peut choisir de redemander l'accès. */
  const [rejoining, setRejoining] = useState(false);
  const started = useRef(false);

  const load = useCallback((secret: string | null) => {
    setPhase({ kind: 'loading' });
    (secret ? openGuestSession(secret) : currentGuestSession()).then(
      (state) => {
        setPhase({ kind: 'ready', state });
      },
      (error: unknown) => {
        setPhase({ kind: 'ended', reason: endReason(error) });
      },
    );
  }, []);

  useEffect(() => {
    // Une seule lecture du fragment (StrictMode rejoue les effets en développement).
    if (started.current) return;
    started.current = true;
    load(takeSecretFromLocation());
  }, [load]);

  const end = useCallback((reason: GuestEnd) => {
    setPhase({ kind: 'ended', reason });
  }, []);

  const onEvent = useCallback((event: GuestEvent) => {
    if (event.type === 'state') setPhase({ kind: 'ready', state: event.state });
    if (event.type === 'closed') setPhase({ kind: 'ended', reason: event.reason });
  }, []);
  const streaming = phase.kind === 'ready';
  const stream = useEventStream(
    streaming ? '/api/guest/events' : null,
    (data) => guestEventSchema.parse(data),
    onEvent,
  );

  if (phase.kind === 'loading') {
    return (
      <main className="app-centered">
        <Card padding="lg" className="app-narrow" as="div">
          <BrandMark />
          <LoadingRegion label="Vérification de votre lien">
            <div className="av-stack av-stack--sm">
              <Skeleton width="70%" height={24} />
              <Skeleton width="40%" height={16} />
            </div>
          </LoadingRegion>
        </Card>
      </main>
    );
  }

  if (phase.kind === 'ended') {
    return (
      <GuestMessage
        reason={phase.reason}
        action={
          phase.reason === 'NETWORK' ||
          phase.reason === 'INTERNAL_ERROR' ||
          phase.reason === 'LEFT' ? (
            <RetryButton
              label={phase.reason === 'LEFT' ? 'Revenir dans la visio' : 'Réessayer'}
              onRetry={() => {
                load(null);
              }}
            />
          ) : undefined
        }
      />
    );
  }

  const { state } = phase;
  const admission = state.admission === 'REMOVED' && rejoining ? 'NONE' : state.admission;
  switch (admission) {
    case 'NONE':
      return (
        <PrejoinView
          state={state}
          pending={requesting}
          error={requestError}
          onRequest={(displayName) => {
            setRequesting(true);
            setRequestError(null);
            requestAdmission(displayName)
              .then((next) => {
                setRejoining(false);
                setPhase({ kind: 'ready', state: next });
              })
              .catch((error: unknown) => {
                const reason = endReason(error);
                if (
                  reason === 'ADMISSION_ALREADY_ACTIVE' ||
                  reason === 'NETWORK' ||
                  reason === 'VALIDATION_FAILED'
                ) {
                  setRequestError(
                    error instanceof ApiClientError ? error.message : 'Erreur inattendue.',
                  );
                } else {
                  setPhase({ kind: 'ended', reason });
                }
              })
              .finally(() => {
                setRequesting(false);
              });
          }}
        />
      );
    case 'PENDING':
      return (
        <WaitingView
          state={state}
          stream={stream.status}
          cancelling={cancelling}
          onRetry={stream.retry}
          onCancel={() => {
            setCancelling(true);
            cancelAdmission()
              .then((next) => {
                setPhase({ kind: 'ready', state: next });
              })
              .catch((error: unknown) => {
                setPhase({ kind: 'ended', reason: endReason(error) });
              })
              .finally(() => {
                setCancelling(false);
              });
          }}
        />
      );
    case 'ADMITTED':
      return (
        <GuestRoom
          state={state}
          onEnd={end}
          onRefresh={() => {
            load(null);
          }}
        />
      );
    case 'DENIED':
      return <GuestMessage reason="ADMISSION_DENIED" />;
    case 'REMOVED':
      return (
        <GuestMessage
          reason="REMOVED_FROM_ROOM"
          action={
            <RetryButton
              label="Demander à revenir"
              onRetry={() => {
                setRejoining(true);
              }}
            />
          }
        />
      );
  }
}

/** Salle de l'invité admis : jeton court demandé à l'entrée, renouvelé à la reconnexion. */
function GuestRoom({
  state,
  onEnd,
  onRefresh,
}: {
  state: GuestState;
  onEnd: (reason: GuestEnd) => void;
  /** Relit l'état serveur : la cause exacte d'une éjection (retrait, révocation, fin). */
  onRefresh: () => void;
}) {
  const [devices] = useState(loadDevicePreferences);
  const [connection, setConnection] = useState<LivekitConnection | null>(null);
  const [lost, setLost] = useState(false);

  const requestToken = useCallback(
    () =>
      fetchGuestToken().then(setConnection, (error: unknown) => {
        onEnd(endReason(error));
      }),
    [onEnd],
  );

  useEffect(() => {
    void requestToken();
  }, [requestToken]);

  if (lost) {
    return (
      <ConnectionLost
        onRejoin={() => {
          setLost(false);
          setConnection(null);
          void requestToken();
        }}
      />
    );
  }
  if (!connection) {
    return (
      <main className="app-centered">
        <Card padding="lg" className="app-narrow" as="div">
          <BrandMark />
          <PageHeading documentTitle="Vous êtes admis">Vous êtes admis</PageHeading>
          <Callout tone="success" title="Connexion à la salle…" live="status" />
        </Card>
      </main>
    );
  }
  return (
    <RoomView
      connection={connection}
      title={state.meeting.title}
      participantRole="guest"
      chatEnabled={state.meeting.chatEnabled}
      screenShareEnabled={state.meeting.screenShareEnabled}
      devices={devices}
      onLeave={() => {
        onEnd('LEFT');
        void leaveAsGuest().catch(() => undefined);
      }}
      onDisconnected={(reason) => {
        const outcome = disconnectOutcome(reason);
        if (outcome === 'removed' || outcome === 'ended') onRefresh();
        else if (outcome === 'lost') setLost(true);
      }}
    />
  );
}
